import {
    AlertCircleIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    LayoutDashboardIcon,
    PinIcon,
    XIcon,
} from "lucide-react";
import {Component, type ErrorInfo, type ReactNode, useCallback, useState} from "react";

import {Card, CardContent} from "@components/ui/card";
import {Skeleton} from "@components/ui/skeleton";
import {
    deleteSandboxArtifact,
    type SandboxArtifact,
    toggleSandboxPin,
    useVNextTabArtifacts,
} from "@hooks/use-vnext";

import {GenericReportCard} from "./GenericReportCard";
import {normalizeArtifactReport} from "./normalize-artifact";

interface PricingTabProps {
    workspaceId: string;
    scenarioId: string;
    actualMonths?: number;
    forecastMonths?: number;
}

/* ── Error Boundary ──────────────────────────────────────── */

class ArtifactErrorBoundary extends Component<
    {children: ReactNode},
    {hasError: boolean; error: string}
> {
    state = {hasError: false, error: ""};

    static getDerivedStateFromError(error: Error) {
        return {hasError: true, error: error.message};
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error("PricingTab artifact render error:", error, info);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                    <AlertCircleIcon className="h-6 w-6 text-red-400 mb-1" />
                    <p className="text-xs text-red-600">Failed to render artifact</p>
                    <p className="text-[10px] text-red-400 mt-0.5 max-w-md">{this.state.error}</p>
                    <button
                        onClick={() => this.setState({hasError: false, error: ""})}
                        className="mt-2 text-xs text-[#209DB3] hover:underline"
                    >
                        Try again
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

/* ── Main Component ──────────────────────────────────────── */

export function PricingTab({workspaceId}: PricingTabProps) {
    const {
        artifacts: pricingArtifacts,
        isLoading: artifactsLoading,
        refresh: refreshArtifacts,
    } = useVNextTabArtifacts(workspaceId, "pricing");

    const [expandedArtifacts, setExpandedArtifacts] = useState<Set<string>>(new Set());

    const toggleCollapse = useCallback((artifactId: string) => {
        setExpandedArtifacts(prev => {
            const next = new Set(prev);
            if (next.has(artifactId)) next.delete(artifactId);
            else next.add(artifactId);
            return next;
        });
    }, []);

    const handleDeleteArtifact = useCallback(
        async (artifactId: string) => {
            await deleteSandboxArtifact(workspaceId, artifactId);
            refreshArtifacts();
        },
        [workspaceId, refreshArtifacts],
    );

    const handleTogglePin = useCallback(
        async (artifactId: string, currentlyPinned: boolean) => {
            await toggleSandboxPin(workspaceId, artifactId, !currentlyPinned);
            refreshArtifacts();
        },
        [workspaceId, refreshArtifacts],
    );

    if (artifactsLoading) {
        return (
            <div className="grid gap-4 md:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                    <Card key={i}>
                        <CardContent className="p-4">
                            <Skeleton className="h-4 w-20 mb-2" />
                            <Skeleton className="h-8 w-32" />
                        </CardContent>
                    </Card>
                ))}
            </div>
        );
    }

    return (
        <div className="flex-1 min-h-0 min-w-0 space-y-6 overflow-y-auto">
            {pricingArtifacts.length > 0 ? (
                <div className="space-y-3">
                    {pricingArtifacts.map((artifact: SandboxArtifact) => {
                        const normalizedReport = normalizeArtifactReport(artifact.report_json);
                        const isCollapsed = !expandedArtifacts.has(artifact.id);
                        return (
                            <ArtifactErrorBoundary key={artifact.id}>
                                <div className="relative group rounded-lg border border-[#209DB3]/20 overflow-hidden">
                                    <button
                                        onClick={() => toggleCollapse(artifact.id)}
                                        className="flex w-full items-center gap-2 bg-[#209DB3] px-4 py-2.5 text-left text-white hover:bg-[#1a8a9e] transition-colors"
                                    >
                                        {isCollapsed ? (
                                            <ChevronDownIcon className="h-4 w-4 shrink-0" />
                                        ) : (
                                            <ChevronUpIcon className="h-4 w-4 shrink-0" />
                                        )}
                                        <span className="flex-1 text-sm font-semibold truncate">{artifact.title}</span>
                                        <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                                            {artifact.created_at && (
                                                <span className="text-[10px] text-white/70 tabular-nums">
                                                    {new Date(artifact.created_at).toLocaleString("en-AU", {day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"})}
                                                </span>
                                            )}
                                            <button
                                                onClick={() => handleTogglePin(artifact.id, artifact.is_pinned)}
                                                className={`transition-colors ${
                                                    artifact.is_pinned
                                                        ? "text-amber-300 hover:text-amber-200"
                                                        : "text-white/40 hover:text-white/80 opacity-0 group-hover:opacity-100"
                                                }`}
                                                title={artifact.is_pinned ? "Unpin" : "Pin"}
                                            >
                                                <PinIcon className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteArtifact(artifact.id)}
                                                className="text-white/40 hover:text-red-300 transition-colors opacity-0 group-hover:opacity-100"
                                                title="Delete artifact"
                                            >
                                                <XIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </button>
                                    {!isCollapsed && (
                                        <div className="p-4">
                                            {normalizedReport ? (
                                                <GenericReportCard report={normalizedReport} />
                                            ) : (
                                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
                                                    <p>Unsupported artifact format: {artifact.title}</p>
                                                    <details className="mt-2">
                                                        <summary className="cursor-pointer text-[11px] text-amber-800">
                                                            Show raw payload
                                                        </summary>
                                                        <pre className="mt-1 max-h-40 overflow-auto rounded border border-amber-200 bg-amber-100/40 p-2 text-[10px] text-amber-900">
                                                            {JSON.stringify(artifact.report_json, null, 2)}
                                                        </pre>
                                                    </details>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </ArtifactErrorBoundary>
                        );
                    })}
                </div>
            ) : (
                <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                        <LayoutDashboardIcon className="mx-auto mb-3 size-8 text-muted-foreground" />
                        <p className="text-lg font-medium">No Pricing Analysis Yet</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Ask the agent to build a pricing dashboard, or upload CSV data as
                            knowledge docs and prompt the agent to analyse pricing.
                        </p>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
